/**
 * Your goal, your targets, and the weekly check-in that adjusts them.
 *
 * First run is the goal form. After that, targets change only three ways, each
 * recorded as a new target row from today on (history is kept, so past days
 * are judged against what was in force then): a check-in, a goal change, or a
 * manual override.
 */

import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { db, type ProfileRow, type TargetRow } from '../db'
import { addCheckin, addTarget, addWeighin, saveProfile } from '../db/mutations'
import { useModel, useProfile, useTargets } from '../db/queries'
import { useApp } from '../context'
import { checkinDue, weekReview } from '../lib/checkin'
import { todayIso } from '../lib/day'
import { shortDate, ageOn } from '../lib/days'
import { confidenceLabel } from '../lib/expenditure'
import { targetFor, type Model } from '../lib/model'
import { fmtKcal } from '../lib/nutrients'
import {
  ACTIVITY, checkinProposal, isProfileComplete, macrosFor, priorExpenditure, propose, rateBounds,
  weeksToGoal, type Activity, type DietStyle, type GoalType, type ProteinPref, type Sex,
} from '../lib/targets'
import { cmToFtIn, displayToKg, formatRate, formatWeight, ftInToCm, kgToDisplay } from '../lib/units'

export function PlanScreen() {
  const m = useModel()
  const profile = useProfile()
  const targets = useTargets()
  const [editingGoal, setEditingGoal] = useState(false)
  const [overriding, setOverriding] = useState(false)
  const lastCheckin = useLiveQuery(async () => {
    const all = (await db.checkins.orderBy('day').toArray()).filter((c) => !c.deleted_at)
    return all.length ? all[all.length - 1] : null
  }, [])
  if (!m || profile === undefined || targets === undefined || lastCheckin === undefined) return <p className="muted">Loading…</p>

  if (!isProfileComplete(profile) || editingGoal) {
    return <GoalForm profile={profile} model={m} onDone={() => setEditingGoal(false)} firstRun={!isProfileComplete(profile)} />
  }

  const today = m.today
  const current = targetFor(targets, today) as TargetRow | null
  const due = checkinDue(today, lastCheckin?.day ?? null, profile.program_start ?? null, profile.checkin_weekday)

  return (
    <div>
      <h1>Plan</h1>
      {due && <CheckIn model={m} profile={profile} current={current} lastCheckin={lastCheckin?.day ?? null} />}

      <div className="card">
        <div className="row"><b>Daily targets</b><span className="muted small">since {current ? shortDate(current.effective_from) : '—'}</span></div>
        {current ? (
          <>
            <div className="big num" style={{ marginTop: '.4rem' }}>{fmtKcal(current.kcal)} <span className="muted" style={{ fontSize: '1rem', fontWeight: 400 }}>kcal</span></div>
            <div className="grid3 small" style={{ marginTop: '.5rem' }}>
              <span><b style={{ color: 'var(--protein)' }}>Protein</b><br />{current.protein_g} g</span>
              <span><b style={{ color: 'var(--carbs)' }}>Carbs</b><br />{current.carbs_g} g</span>
              <span><b style={{ color: 'var(--fat)' }}>Fat</b><br />{current.fat_g} g</span>
            </div>
            {current.fibre_g ? <div className="muted small" style={{ marginTop: '.4rem' }}>Fibre {current.fibre_g} g</div> : null}
          </>
        ) : <p className="muted">No target yet.</p>}
        <button className="linky" style={{ marginTop: '.8rem' }} onClick={() => setOverriding(!overriding)}>
          {overriding ? 'Cancel' : 'Set targets by hand'}</button>
        {overriding && current && <Override current={current} profile={profile} model={m} onDone={() => setOverriding(false)} />}
      </div>

      <TrainingShiftCard profile={profile} model={m} />

      <GoalSummary profile={profile} model={m} onEdit={() => setEditingGoal(true)} />

      <div className="card" style={{ marginTop: '1rem' }}>
        <b>Expenditure</b>
        <div className="row" style={{ marginTop: '.3rem' }}>
          <span className="big num">{m.eNow ? fmtKcal(m.eNow) : '—'}</span>
          <span className="muted small">{confidenceLabel(m.confidence)} confidence</span>
        </div>
        <p className="muted small" style={{ marginBottom: 0 }}>
          Started from a formula estimate ({m.prior ? fmtKcal(m.prior) : '—'} kcal) and learns from what you log and
          what the scale does. It needs about two weeks of logging and weigh-ins to settle.
        </p>
        {!due && lastCheckin && <p className="muted small">Last check-in {shortDate(lastCheckin.day)}.</p>}
      </div>

      <h2>Target history</h2>
      <div className="card" style={{ padding: '.2rem .5rem' }}>
        {[...targets].reverse().slice(0, 12).map((t) => (
          <div key={t.id} className="food-row">
            <div><div className="name num">{fmtKcal(t.kcal)} kcal</div>
              <div className="muted small">P {t.protein_g} · C {t.carbs_g} · F {t.fat_g} g · {sourceText(t.source)}</div></div>
            <span className="muted small">{shortDate(t.effective_from)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function TrainingShiftCard({ profile, model }: { profile: ProfileRow; model: Model }) {
  const { user } = useApp()
  const on = profile.training_shift !== false
  const s = model.shift
  const base = targetFor(model.targets, model.today)
  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="row"><b>Training days</b>
        <div className="pill-row">
          <button className={`pill ${on ? 'on' : ''}`} onClick={() => void saveProfile(user.id, { training_shift: true })}>Shift</button>
          <button className={`pill ${!on ? 'on' : ''}`} onClick={() => void saveProfile(user.id, { training_shift: false })}>Off</button>
        </div>
      </div>
      <p className="muted small" style={{ marginBottom: 0 }}>
        Workouts logged in Lift come with an estimated burn. Your expenditure already includes training, so it's never
        added on top — instead, calories move from rest days to training days and the week totals the same.
      </p>
      {on && (s.active && base ? (
        <p className="small" style={{ marginBottom: 0 }}>
          ~{s.trainingDaysPerWeek} Lift workouts a week: rest days <b className="num">{fmtKcal(base.kcal - s.restCut)}</b> kcal
          (−{s.restCut}); training days get half their estimated burn back, up to +400.
        </p>
      ) : <p className="small" style={{ marginBottom: 0 }}>Not shifting yet: {s.reason?.toLowerCase()}.</p>)}
    </div>
  )
}

function sourceText(s: TargetRow['source']) {
  return s === 'checkin' ? 'check-in' : s === 'goal_change' ? 'goal change' : s === 'manual' ? 'set by hand' : 'starting target'
}

function GoalSummary({ profile, model, onEdit }: { profile: ProfileRow; model: Model; onEdit: () => void }) {
  const { unit } = useApp()
  const eta = model.trendNow ? weeksToGoal(model.trendNow, profile.goal_weight_kg, model.rate21) : null
  const goalText = profile.goal_type === 'maintain' ? 'Maintain weight'
    : `${profile.goal_type === 'lose' ? 'Lose' : 'Gain'} ${formatRate(Math.abs(profile.goal_rate_kg_wk ?? 0), unit).replace(/^[+−]/, '')}`
  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="row"><b>Goal</b><button className="linky" onClick={onEdit}>Change</button></div>
      <p style={{ margin: '.3rem 0' }}>{goalText}{profile.goal_weight_kg ? ` · to ${formatWeight(profile.goal_weight_kg, unit)}` : ''}</p>
      <div className="muted small">
        Trend {model.trendNow ? formatWeight(model.trendNow, unit) : '—'} · {model.rate21 !== null ? formatRate(model.rate21, unit) : 'rate needs 3+ weigh-ins'}
        {eta !== null && eta > 0 ? ` · about ${eta} week${eta === 1 ? '' : 's'} to goal at this rate` : ''}
        {eta === 0 ? ' · at your goal weight' : ''}
      </div>
    </div>
  )
}

// --- check-in -------------------------------------------------------------------

function CheckIn({ model, profile, current, lastCheckin }: {
  model: Model; profile: ProfileRow; current: TargetRow | null; lastCheckin: string | null
}) {
  const { unit } = useApp()
  const review = weekReview(model, lastCheckin)
  const kg = model.trendNow ?? 80
  const today = model.today
  const proposal = propose({ expenditure: model.eNow ?? model.prior ?? 2000, kg, profile, today })
  const decision = checkinProposal({ current, proposal, loggedDays: review.loggedDays, weighIns: review.weighIns, profile, kg })
  const [kcalText, setKcalText] = useState(String(decision.kcal))
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const kcal = editing ? Math.round(Number(kcalText)) : decision.kcal
  const macros = editing ? macrosFor(kcal, profile, kg) : decision.macros

  const commit = async (action: 'accepted' | 'edited' | 'kept') => {
    setBusy(true)
    try {
      let target_id: string | null = current?.id ?? null
      if (action !== 'kept') {
        const t = await addTarget({ effective_from: today, kcal, protein_g: macros.protein_g, carbs_g: macros.carbs_g,
          fat_g: macros.fat_g, fibre_g: Math.round((14 * kcal) / 1000), goal_type: profile.goal_type,
          goal_rate_kg_wk: proposal.rate, expenditure: model.eNow, source: 'checkin' })
        target_id = t.id
      }
      await addCheckin({ day: today, action, target_id,
        inputs: { ...review, confidence: model.confidence },
        proposed: { kcal: decision.kcal, kind: decision.kind, reason: decision.reason } })
    } finally { setBusy(false) }
  }

  const delta = review.trendStart !== null && review.trendEnd !== null ? review.trendEnd - review.trendStart : null
  return (
    <div className="card" style={{ borderColor: 'var(--accent)', marginBottom: '1rem' }}>
      <b>Weekly check-in</b>
      <div className="grid2 small" style={{ marginTop: '.6rem' }}>
        <div><div className="muted">Average intake</div><div className="num">{review.avgIntake ? `${fmtKcal(review.avgIntake)} kcal` : '—'}</div>
          <div className="muted">{review.loggedDays}/7 days counted</div></div>
        <div><div className="muted">Trend change</div><div className="num">{delta !== null ? formatRate(delta, unit).replace('/wk', '') : '—'}</div>
          <div className="muted">{review.weighIns} weigh-in{review.weighIns === 1 ? '' : 's'}</div></div>
        <div><div className="muted">Expenditure</div>
          <div className="num">{review.eOld ? fmtKcal(review.eOld) : '—'} → {review.eNow ? fmtKcal(review.eNow) : '—'}</div>
          <div className="muted">{confidenceLabel(model.confidence)} confidence</div></div>
        <div><div className="muted">On target</div><div className="num">{review.onTarget}/{review.loggedDays || 0} days</div></div>
      </div>
      <p style={{ marginBottom: '.2rem' }}>{decision.reason}</p>
      {editing ? (
        <div className="row" style={{ gap: '.5rem' }}>
          <input aria-label="Calories" inputMode="numeric" value={kcalText} onChange={(e) => setKcalText(e.target.value)} />
          <span className="muted">kcal</span>
        </div>
      ) : (
        <div className="big num">{fmtKcal(kcal)} kcal</div>
      )}
      <div className="muted small">P {macros.protein_g} · C {macros.carbs_g} · F {macros.fat_g} g</div>
      {proposal.warnings.length > 0 && <div className="small" style={{ color: 'var(--warn)', marginTop: '.3rem' }}>{proposal.warnings.join(' ')}</div>}
      <div className="row" style={{ gap: '.5rem', flexWrap: 'wrap' }}>
        <button disabled={busy || (editing && !(kcal >= 800 && kcal <= 8000))}
          onClick={() => void commit(editing ? 'edited' : decision.kind === 'keep' ? 'kept' : 'accepted')}>
          {editing ? 'Save' : decision.kind === 'keep' ? 'OK' : 'Accept'}
        </button>
        {!editing && <button className="secondary" onClick={() => setEditing(true)}>Edit</button>}
        {!editing && decision.kind === 'change' && <button className="secondary" disabled={busy} onClick={() => void commit('kept')}>Keep current</button>}
      </div>
    </div>
  )
}

// --- manual override -------------------------------------------------------------

function Override({ current, profile, model, onDone }: { current: TargetRow; profile: ProfileRow; model: Model; onDone: () => void }) {
  const [v, setV] = useState({ kcal: String(current.kcal), protein_g: String(current.protein_g),
    carbs_g: String(current.carbs_g), fat_g: String(current.fat_g) })
  const n = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, Number(x)])) as Record<string, number>
  const implied = 4 * n.protein_g + 4 * n.carbs_g + 9 * n.fat_g
  const ok = n.kcal >= 800 && n.kcal <= 8000 && [n.protein_g, n.carbs_g, n.fat_g].every((x) => x >= 0)
  return (
    <div>
      <div className="grid2">
        {(['kcal', 'protein_g', 'carbs_g', 'fat_g'] as const).map((k) => (
          <div key={k}>
            <label htmlFor={`ov-${k}`}>{({ kcal: 'Calories', protein_g: 'Protein (g)', carbs_g: 'Carbs (g)', fat_g: 'Fat (g)' })[k]}</label>
            <input id={`ov-${k}`} inputMode="numeric" value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
      <button className="linky" style={{ marginTop: '.6rem' }} onClick={() => {
        const mm = macrosFor(n.kcal, profile, model.trendNow ?? 80)
        setV((s) => ({ ...s, protein_g: String(mm.protein_g), carbs_g: String(mm.carbs_g), fat_g: String(mm.fat_g) }))
      }}>Fill macros from calories</button>
      {Math.abs(implied - n.kcal) > 60 && <p className="muted small">Macros add up to {Math.round(implied)} kcal.</p>}
      <button style={{ width: '100%' }} disabled={!ok} onClick={async () => {
        await addTarget({ effective_from: todayIso(), kcal: n.kcal, protein_g: n.protein_g, carbs_g: n.carbs_g, fat_g: n.fat_g,
          fibre_g: Math.round((14 * n.kcal) / 1000), goal_type: profile.goal_type, goal_rate_kg_wk: profile.goal_rate_kg_wk,
          expenditure: model.eNow, source: 'manual' })
        onDone()
      }}>Use these from today</button>
      <p className="muted small">Check-ins will still suggest changes; you can keep yours each time.</p>
    </div>
  )
}

// --- goal form ---------------------------------------------------------------------

function GoalForm({ profile, model, onDone, firstRun }: {
  profile: ProfileRow | null; model: Model; onDone: () => void; firstRun: boolean
}) {
  const { user, unit } = useApp()
  const today = todayIso()
  const [sex, setSex] = useState<Sex | ''>(profile?.sex ?? '')
  const [birth, setBirth] = useState(profile?.birth_date ?? '')
  const h0 = profile?.height_cm ?? null
  const [cm, setCm] = useState(h0 ? String(h0) : '')
  const [ft, setFt] = useState(h0 ? String(cmToFtIn(h0).ft) : '')
  const [inch, setInch] = useState(h0 ? String(cmToFtIn(h0).inch) : '')
  const [weight, setWeight] = useState(model.trendNow ? (kgToDisplay(model.trendNow, unit) ?? 0).toFixed(1) : '')
  const [activity, setActivity] = useState<Activity>(profile?.activity ?? 'light')
  const [goal, setGoal] = useState<GoalType>(profile?.goal_type ?? 'lose')
  const [rate, setRate] = useState(profile?.goal_rate_kg_wk ? String(profile.goal_rate_kg_wk) : '0.5')
  const [goalW, setGoalW] = useState(profile?.goal_weight_kg ? (kgToDisplay(profile.goal_weight_kg, unit) ?? 0).toFixed(1) : '')
  const [protein, setProtein] = useState<ProteinPref>(profile?.protein_pref ?? 'medium')
  const [diet, setDiet] = useState<DietStyle>(profile?.diet_style ?? 'balanced')
  const [weekdayN, setWeekdayN] = useState(profile?.checkin_weekday ?? 0)
  const [busy, setBusy] = useState(false)

  const heightCm = unit === 'lb' ? (ft ? ftInToCm(Number(ft), Number(inch || 0)) : null) : (cm ? Number(cm) : null)
  const kg = displayToKg(Number(weight), unit)
  const age = birth ? ageOn(birth, today) : null
  const bounds = kg ? rateBounds(kg) : null
  const rates = goal === 'lose' ? [0.25, 0.5, 0.75, 1.0] : goal === 'gain' ? [0.1, 0.25, 0.5] : [0]
  const ok = sex && age !== null && age >= 18 && age < 100 && heightCm && heightCm >= 120 && heightCm <= 230 && kg && kg >= 30 && kg <= 350

  const draft = ok ? {
    sex: sex as Sex, birth_date: birth, height_cm: heightCm!, activity, goal_type: goal,
    goal_rate_kg_wk: goal === 'maintain' ? 0 : Number(rate), goal_weight_kg: goalW ? displayToKg(Number(goalW), unit) : null,
    protein_pref: protein, diet_style: diet,
  } : null
  const E = draft ? (model.eNow && !firstRun ? model.eNow : priorExpenditure(draft, kg!, today)) : null
  const preview = draft && E ? propose({ expenditure: E, kg: kg!, profile: draft, today }) : null

  const save = async () => {
    if (!draft || !preview || !kg) return
    setBusy(true)
    try {
      if (!model.trendNow || Math.abs((model.trendNow ?? 0) - kg) > 0.05) {
        if (!model.weighDays.has(today)) await addWeighin(kg, new Date().toISOString())
      }
      await saveProfile(user.id, { ...draft, checkin_weekday: weekdayN, program_start: profile?.program_start ?? today })
      await addTarget({ effective_from: today, kcal: preview.kcal, protein_g: preview.protein_g, carbs_g: preview.carbs_g,
        fat_g: preview.fat_g, fibre_g: preview.fibre_g, goal_type: goal, goal_rate_kg_wk: preview.rate,
        expenditure: E, source: firstRun ? 'initial' : 'goal_change' })
      onDone()
    } finally { setBusy(false) }
  }

  return (
    <div>
      <h1>{firstRun ? 'Set up your plan' : 'Change goal'}</h1>
      {firstRun && <p className="muted">Macro starts from a standard formula, then learns your real expenditure from what you log and weigh. Weigh-ins are shared with Lift.</p>}
      <div className="card">
        <label>Sex (for the formula)</label>
        <div className="pill-row">
          {(['male', 'female'] as const).map((s) => (
            <button key={s} className={`pill ${sex === s ? 'on' : ''}`} onClick={() => setSex(s)}>{s === 'male' ? 'Male' : 'Female'}</button>
          ))}
        </div>
        <label htmlFor="g-birth">Birth date</label>
        <input id="g-birth" type="date" value={birth} max={today} onChange={(e) => setBirth(e.target.value)} />
        <label>Height</label>
        {unit === 'lb' ? (
          <div className="grid2">
            <input aria-label="Feet" inputMode="numeric" placeholder="ft" value={ft} onChange={(e) => setFt(e.target.value)} />
            <input aria-label="Inches" inputMode="numeric" placeholder="in" value={inch} onChange={(e) => setInch(e.target.value)} />
          </div>
        ) : <input aria-label="Height in cm" inputMode="decimal" placeholder="cm" value={cm} onChange={(e) => setCm(e.target.value)} />}
        <label htmlFor="g-w">Current weight ({unit})</label>
        <input id="g-w" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
        {model.trendNow && <p className="muted small">Trend from your weigh-ins: {formatWeight(model.trendNow, unit)}</p>}
        <label htmlFor="g-act">Activity (outside logged exercise)</label>
        <select id="g-act" value={activity} onChange={(e) => setActivity(e.target.value as Activity)}>
          {(Object.keys(ACTIVITY) as Activity[]).map((a) => <option key={a} value={a}>{ACTIVITY[a].label} — {ACTIVITY[a].hint}</option>)}
        </select>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <label style={{ marginTop: 0 }}>Goal</label>
        <div className="pill-row">
          {(['lose', 'maintain', 'gain'] as const).map((g) => (
            <button key={g} className={`pill ${goal === g ? 'on' : ''}`} onClick={() => {
              setGoal(g); setRate(g === 'lose' ? '0.5' : g === 'gain' ? '0.25' : '0')
            }}>{g === 'lose' ? 'Lose' : g === 'maintain' ? 'Maintain' : 'Gain'}</button>
          ))}
        </div>
        {goal !== 'maintain' && (
          <>
            <label>Rate per week</label>
            <div className="pill-row">
              {rates.map((r) => {
                const over = bounds && (goal === 'lose' ? r > bounds.maxLose : r > bounds.maxGain)
                return <button key={r} className={`pill ${Number(rate) === r ? 'on' : ''}`} disabled={!!over}
                  onClick={() => setRate(String(r))}>{formatRate(r, unit).replace('+', '')}</button>
              })}
            </div>
            <label htmlFor="g-gw">Goal weight ({unit}, optional)</label>
            <input id="g-gw" inputMode="decimal" value={goalW} onChange={(e) => setGoalW(e.target.value)} />
          </>
        )}
        <label>Protein</label>
        <div className="pill-row">
          {(['low', 'medium', 'high'] as const).map((p) => (
            <button key={p} className={`pill ${protein === p ? 'on' : ''}`} onClick={() => setProtein(p)}>
              {p === 'low' ? 'Moderate' : p === 'medium' ? 'High' : 'Very high'}</button>
          ))}
        </div>
        <label>Diet style</label>
        <div className="pill-row">
          {(['balanced', 'lowfat', 'lowcarb', 'keto'] as const).map((d) => (
            <button key={d} className={`pill ${diet === d ? 'on' : ''}`} onClick={() => setDiet(d)}>
              {({ balanced: 'Balanced', lowfat: 'Low fat', lowcarb: 'Low carb', keto: 'Keto' })[d]}</button>
          ))}
        </div>
        <label htmlFor="g-wd">Check-in day</label>
        <select id="g-wd" value={weekdayN} onChange={(e) => setWeekdayN(Number(e.target.value))}>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      </div>

      {preview && E && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <b>Your starting target</b>
          <div className="big num" style={{ marginTop: '.3rem' }}>{fmtKcal(preview.kcal)} kcal</div>
          <div className="small">P {preview.protein_g} · C {preview.carbs_g} · F {preview.fat_g} g · fibre {preview.fibre_g} g</div>
          <p className="muted small" style={{ marginBottom: 0 }}>
            {firstRun || !model.eNow ? `Estimated expenditure ${fmtKcal(E)} kcal (Mifflin-St Jeor × ${ACTIVITY[activity].factor})`
              : `From your learned expenditure, ${fmtKcal(E)} kcal`}
            {preview.rate !== 0 ? `, ${preview.rate < 0 ? '−' : '+'}${fmtKcal(Math.abs(preview.rate) * 1100)} kcal a day for ${formatRate(preview.rate, unit)}.` : '.'}
          </p>
          {preview.warnings.length > 0 && <p className="small" style={{ color: 'var(--warn)' }}>{preview.warnings.join(' ')}</p>}
        </div>
      )}

      <button style={{ width: '100%' }} disabled={!ok || busy} onClick={save}>{firstRun ? 'Start' : 'Save goal'}</button>
      {!firstRun && <button className="secondary" style={{ width: '100%' }} onClick={onDone}>Cancel</button>}
      {!ok && <p className="muted small">Fill in sex, birth date (18+), height and weight.</p>}
    </div>
  )
}
